import type * as React from 'react'
import * as ProgressPrimitive from '@rn-primitives/progress'
import { Platform, View } from 'react-native'
import Animated, {
  Extrapolation,
  interpolate,
  useAnimatedStyle,
  useDerivedValue,
  withSpring,
} from 'react-native-reanimated'
import { cn } from '~/lib/utils'

function Progress({
  className,
  value,
  indicatorClassName,
  ...props
}: ProgressPrimitive.RootProps & {
  ref?: React.RefObject<ProgressPrimitive.RootRef>
  indicatorClassName?: string
}) {
  return (
    <ProgressPrimitive.Root
      className={cn(
        'relative h-[7px] w-full overflow-hidden rounded-full bg-tertiary-foreground',
        className
      )}
      {...props}>
      <Indicator value={value} className={indicatorClassName} />
    </ProgressPrimitive.Root>
  )
}

export { Progress }

function Indicator({ value, className }: { value: number | undefined | null; className?: string }) {
  const progress = useDerivedValue(() => value ?? 0)

  const indicator = useAnimatedStyle(() => {
    return {
      width: withSpring(
        `${interpolate(progress.value, [0, 100], [1, 100], Extrapolation.CLAMP)}%`,
        { overshootClamping: true }
      ),
    }
  })

  if (Platform.OS === 'web') {
    return (
      <View
        className={cn('h-full w-full flex-1 rounded-[3px] bg-accent web:transition-all', className)}
        style={{ transform: `translateX(-${100 - (value ?? 0)}%)` }}>
        <ProgressPrimitive.Indicator className={cn('h-full w-full', className)} />
      </View>
    )
  }

  return (
    <ProgressPrimitive.Indicator asChild>
      <Animated.View style={indicator} className={cn('h-full bg-accent', className)} />
    </ProgressPrimitive.Indicator>
  )
}
